import { useState, useEffect, useMemo, useCallback } from 'react';
import client from '../services/apiClient';
import { api } from '../services/api';
/**
 * Manages file state for the e-pilot chat: available files, selection,
 * search, and recent file stack.
 *
 * @param {Object} params
 * @param {Object} params.context - Chat context with project_id
 * @param {Array}  params.initialFiles - Files passed as props (merged with fetched)
 */
export default function useChatFiles({ context, initialFiles = [] }) {
  const [availableFiles, setAvailableFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [recentFileIds, setRecentFileIds] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  const fetchFiles = useCallback(async () => {
    try {
      setFetchError(null);
      const projectId = context?.project_id;
      if (!projectId) {
        setAvailableFiles([]);
        return;
      }
      // Fetch documents + CAD drawings in parallel
      const [docResponse, cadResponse] = await Promise.allSettled([
        api.getFiles(projectId),
        api.getCADDrawings(projectId, {}),
      ]);

      const docsData = docResponse.status === 'fulfilled'
        ? (Array.isArray(docResponse.value.data) ? docResponse.value.data : Array.isArray(docResponse.value.data?.items) ? docResponse.value.data.items : [])
        : [];
      const cadData = cadResponse.status === 'fulfilled'
        ? (Array.isArray(cadResponse.value.data) ? cadResponse.value.data : [])
        : [];

      const mappedDocs = docsData.map(f => ({
        file_id: f.document_id,
        file_name: f.name || f.title,
        file_type: f.type || 'other',
        updated: f.created_at,
      }));
      const mappedCad = cadData.map(d => ({
        file_id: d.drawing_id,
        file_name: d.name,
        file_type: 'cad',
        updated: d.created_at,
      }));

      // Merge and deduplicate by file_id
      const seen = new Set();
      const mappedFiles = [...mappedDocs, ...mappedCad].filter(f => {
        if (seen.has(f.file_id)) return false;
        seen.add(f.file_id);
        return true;
      });

      mappedFiles.sort((a, b) => new Date(b.updated) - new Date(a.updated));
      setAvailableFiles(mappedFiles);
      setRecentFileIds(mappedFiles.slice(0, 10).map(f => f.file_id));
    } catch (error) {
      console.error('Error fetching files for chat:', error);
      setFetchError('Failed to load files');
      setAvailableFiles([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.project_id]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Merge prop files with fetched files (deduplicate by ID)
  const allFiles = useMemo(() => {
    const merged = [...availableFiles];
    initialFiles.forEach(f => {
      if (!merged.find(af => af.file_id === f.file_id)) {
        merged.push(f);
      }
    });
    return merged;
  }, [availableFiles, initialFiles]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const formData = new FormData();
      formData.append('query', searchQuery);
      const response = await client.post('/api/v1/search', formData);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  // Filtered + searched file list
  const displayedFiles = useMemo(() => {
    if (searchQuery.trim()) {
      // When searching, show ALL matching files (no limit) so users can find any file
      const results = allFiles.filter(file => {
        const lowerQuery = searchQuery.toLowerCase();
        const nameMatch = file.file_name.toLowerCase().includes(lowerQuery);
        const semanticMatch = searchResults.some(result => {
          const uri = result.location?.s3Location?.uri;
          if (uri) {
            const searchPattern = '/' + file.file_id;
            return uri.endsWith(searchPattern) || uri.includes(searchPattern + '.');
          }
          return false;
        });
        return nameMatch || semanticMatch;
      });
      return results;
    }

    // Default: show last 10 recently uploaded files
    return recentFileIds
      .map(id => allFiles.find(f => f.file_id === id))
      .filter(Boolean)
      .slice(0, 10);
  }, [searchQuery, allFiles, searchResults, recentFileIds]);

  const toggleFileSelection = useCallback((fileId) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
    setRecentFileIds(prev => {
      const newStack = prev.filter(id => id !== fileId);
      return [fileId, ...newStack].slice(0, 10);
    });
  }, []);

  return {
    availableFiles,
    selectedFiles,
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    allFiles,
    displayedFiles,
    fetchFiles,
    handleSearch,
    toggleFileSelection,
    fetchError,
  };
}
